import React, { useState } from "react";

function UploadFile() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setError("");
      setSuccess("");
    } else {
      setError("Please select a valid PDF file");
      setFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError("Please select a PDF file");
      return;
    }

    setUploading(true);
    setAnalyzing(false);
    setProgress(0);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();
      formData.append("Upload_Project_PDF", file);

      const response = await fetch(
        "https://aiuhf.app.n8n.cloud/webhook/d539a30c-5c63-4f58-b0ff-07226c55420b",
        { method: "POST", body: formData }
      );

      if (!response.ok) throw new Error("Workflow trigger failed");

      setUploading(false);
      setAnalyzing(true);
      setProgress(0);

      // ─── 30 second countdown with progress bar ───
      await new Promise((resolve) => {
        const DURATION = 30000; // 30 seconds
        const INTERVAL = 300;   // update every 300ms
        const steps    = DURATION / INTERVAL;
        let current    = 0;

        const timer = setInterval(() => {
          current += 1;
          setProgress(Math.min(Math.round((current / steps) * 100), 100));

          if (current >= steps) {
            clearInterval(timer);
            resolve();
          }
        }, INTERVAL);
      });

      setAnalyzing(false);
      setProgress(0);
      setSuccess("File analyzed successfully! Data extracted and processed.");
      setFile(null);
      document.getElementById("file-input").value = "";
    } catch (err) {
      setError("Failed to process file: " + err.message);
      setUploading(false);
      setAnalyzing(false);
      setProgress(0);
    }
  };

  return (
    <div className="container">
      <h1>Upload Document</h1>

      <div className="form-card">
        {error   && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group-single">
            <label htmlFor="file-input">Select PDF File</label>
            <input
              type="file"
              id="file-input"
              accept=".pdf"
              onChange={handleFileChange}
              className="file-input"
              disabled={uploading || analyzing}
            />
          </div>

          {file && !uploading && !analyzing && (
            <div className="selected-file-info">
              <span className="file-icon">📎</span>
              <span className="file-name">{file.name}</span>
              <span className="file-size">({(file.size / 1024).toFixed(2)} KB)</span>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="progress-info">
              <span className="spinner"></span>
              <span className="progress-text">Uploading file...</span>
            </div>
          )}

          {/* Analysis Progress — 30 seconds */}
          {analyzing && (
            <div className="progress-info analyzing">
              <span className="spinner"></span>
              <span className="progress-text">Analyzing document...</span>
              <p className="progress-subtext">
                Extracting project details, client information, and milestones
              </p>

              {/* Progress Bar */}
              <div className="progress-bar-wrapper">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="progress-percent">{progress}%</span>
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn primary"
              disabled={!file || uploading || analyzing}
            >
              {uploading ? (
                <><span className="spinner"></span> Uploading...</>
              ) : analyzing ? (
                <><span className="spinner"></span> Analyzing...</>
              ) : (
                "Upload & Analyze"
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .container {
          max-width: 600px;
          margin: 50px auto;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        }

        h1 {
          text-align: center;
          color: #333;
          margin-bottom: 30px;
        }

        .form-card {
          background: white;
          border-radius: 12px;
          padding: 30px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .form-group-single {
          margin-bottom: 20px;
        }

        label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #333;
        }

        .file-input {
          width: 100%;
          padding: 10px;
          border: 2px dashed #ddd;
          border-radius: 8px;
          cursor: pointer;
          transition: border-color 0.3s;
        }

        .file-input:hover:not(:disabled) {
          border-color: #004687;
        }

        .file-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .selected-file-info {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f5f5f5;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .file-icon { font-size: 24px; }

        .file-name {
          flex: 1;
          font-weight: 500;
          color: #333;
        }

        .file-size {
          color: #666;
          font-size: 14px;
        }

        .progress-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 20px;
          background: #e3f2fd;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .progress-info.analyzing {
          background: #fff3e0;
        }

        .progress-text {
          font-weight: 600;
          color: #1976d2;
          font-size: 16px;
        }

        .analyzing .progress-text {
          color: #f57c00;
        }

        .progress-subtext {
          color: #666;
          font-size: 14px;
          text-align: center;
          margin: 0;
        }

        /* ─── Progress Bar ─── */
        .progress-bar-wrapper {
          width: 100%;
          height: 10px;
          background: rgba(0, 0, 0, 0.1);
          border-radius: 999px;
          overflow: hidden;
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #f57c00, #ff9800);
          border-radius: 999px;
          transition: width 0.3s ease;
        }

        .progress-percent {
          font-size: 13px;
          font-weight: 600;
          color: #f57c00;
        }

        .form-actions {
          display: flex;
          justify-content: center;
        }

        .btn {
          padding: 12px 30px;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .btn.primary {
          background: linear-gradient(135deg, #004687, #003366);
          color: white;
        }

        .btn.primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #003366, #002244);
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          display: inline-block;
        }

        .progress-info .spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(0, 0, 0, 0.1);
          border-top-color: #1976d2;
        }

        .analyzing .spinner {
          border-top-color: #f57c00;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .error-message {
          padding: 12px;
          background: #ffebee;
          color: #c62828;
          border-radius: 8px;
          margin-bottom: 20px;
          border-left: 4px solid #c62828;
        }

        .success-message {
          padding: 12px;
          background: #e8f5e9;
          color: #2e7d32;
          border-radius: 8px;
          margin-bottom: 20px;
          border-left: 4px solid #2e7d32;
        }
      `}</style>
    </div>
  );
}

export default UploadFile;